// Fills the service worker's runtime caches with the static data the core
// practice flows need, so an installed PWA keeps working offline for them
// (the service worker only caches a stroke file once something has asked for
// it). Fetching through the page lets the service worker's own routes
// (src/service-worker.ts: stroke-data, vocab-data) do the caching, so there is
// no cache-name coupling here. Runs once the page is controlled by a service
// worker (including its first activation), skips Data Saver, and
// skips anything already cached.
import { MISSIONS } from "./missions";
import { strokeDataUrl } from "./worksheet/strokeData";

const PUBLIC_URL = process.env.PUBLIC_URL || "";
const CONCURRENCY = 4;

// JLPT levels whose word lists are warmed; N3-N1 are large and are cached
// when first viewed instead.
const WARMED_VOCAB_LEVELS = [5, 4];

const WARMED_MISSION_IDS = ["draw-all-hiragana", "draw-all-katakana", "draw-all-n5-kanji"];

export function offlineWarmupUrls(): string[] {
    const symbols = MISSIONS.filter((mission) => WARMED_MISSION_IDS.includes(mission.id)).flatMap((mission) => [
        ...mission.items,
    ]);
    const vocab = WARMED_VOCAB_LEVELS.flatMap((level) => [
        `${PUBLIC_URL}/vocab-data/n${level}.json`,
        `${PUBLIC_URL}/vocab-data/pt/n${level}.json`,
    ]);
    return [...Array.from(new Set(symbols)).map(strokeDataUrl), ...vocab, `${PUBLIC_URL}/vocab-data/pt/kanji-examples.json`];
}

function saveDataEnabled(): boolean {
    return Boolean((navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData);
}

export async function warmOfflineCaches(): Promise<void> {
    if (!("serviceWorker" in navigator) || typeof caches === "undefined") return;
    if (saveDataEnabled() || navigator.onLine === false) return;
    await navigator.serviceWorker.ready;
    if (!navigator.serviceWorker.controller) return;

    const queue = offlineWarmupUrls();
    async function worker() {
        while (queue.length > 0) {
            const url = queue.shift() as string;
            try {
                if (await caches.match(url)) continue;
                await fetch(url);
            } catch {
                // Best effort: a failed fetch just means it is cached later, on use.
            }
        }
    }
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
}

// Waits for the browser to be idle after load so the warm-up never competes
// with the app's own startup requests.
export function scheduleOfflineWarmup(): void {
    if (!("serviceWorker" in navigator)) return;
    const start = () => {
        void warmOfflineCaches().catch(() => undefined);
    };
    const whenIdle = () => {
        if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(start, { timeout: 10000 });
        else setTimeout(start, 3000);
    };
    const afterLoad = () => {
        if (navigator.serviceWorker.controller) whenIdle();
        else {
            // ready can resolve before clientsClaim takes control. Wait for
            // control so warm-up fetches pass through the caching routes.
            const onControl = () => {
                if (!navigator.serviceWorker.controller) return;
                navigator.serviceWorker.removeEventListener("controllerchange", onControl);
                whenIdle();
            };
            navigator.serviceWorker.addEventListener("controllerchange", onControl);
        }
    };
    if (document.readyState === "complete") afterLoad();
    else window.addEventListener("load", afterLoad, { once: true });
}
